import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Logo import
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

const Feedback = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [authorName, setAuthorName] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    search: ''
  });

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'Question',
    priority: 'Medium',
    user_name: '',
    user_email: ''
  });
  const [newTicketFiles, setNewTicketFiles] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

  // Load tickets on mount and when filters change
  useEffect(() => {
    loadTickets();
  }, [filters]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);
      
      const listToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/feedback?${params}`, {
        headers: listToken ? { Authorization: `Bearer ${listToken}` } : {}
      });
      if (!response.ok) throw new Error('Failed to load tickets');
      
      const data = await response.json();
      setTickets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      setLoading(true);
      const detailToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/feedback/${ticketId}`, {
        headers: detailToken ? { Authorization: `Bearer ${detailToken}` } : {}
      });
      if (!response.ok) throw new Error('Failed to load ticket details');
      
      const data = await response.json();
      setSelectedTicket(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const submitNewTicket = async () => {
    try {
      const formData = new FormData();
      Object.entries(newTicket).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      if (newTicketFiles) {
        Array.from(newTicketFiles).forEach(file => {
          formData.append('attachments', file);
        });
      }
      
      const response = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to submit ticket');
      
      setSubmitModalOpen(false);
      setNewTicket({
        title: '',
        description: '',
        category: 'Question',
        priority: 'Medium',
        user_name: '',
        user_email: ''
      });
      setNewTicketFiles(null);
      loadTickets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit ticket');
    }
  };

  const addResponse = async () => {
    if (!selectedTicket || !responseText.trim() || !authorName.trim()) return;
    
    try {
      const formData = new FormData();
      formData.append('content', responseText);
      formData.append('author_name', authorName);
      
      const response = await fetch(`${API_BASE}/api/feedback/${selectedTicket.id}/responses`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to add response');
      
      setResponseText('');
      loadTicketDetail(selectedTicket.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add response');
    }
  };

  const updateStatus = async (ticketId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/feedback/${ticketId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update status');
      
      if (selectedTicket) {
        loadTicketDetail(ticketId);
      }
      loadTickets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'primary';
      case 'In Progress': return 'warning';
      case 'Resolved': return 'success';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // List View
  if (!selectedTicket) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                Feedback & Support
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setSubmitModalOpen(true)}
                size="large"
              >
                Submit Feedback
              </Button>
            </Box>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              Submit bug reports, feature requests, questions, and other feedback
            </Typography>
          </Grid>

          {/* Filters */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Resolved">Resolved</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filters.category}
                      label="Category"
                      onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      <MenuItem value="Bug Report">Bug Report</MenuItem>
                      <MenuItem value="Feature Request">Feature Request</MenuItem>
                      <MenuItem value="Question">Question</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={filters.priority}
                      label="Priority"
                      onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      <MenuItem value="">All Priorities</MenuItem>
                      <MenuItem value="Critical">Critical</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="Low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    placeholder="Search title or description..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadTickets}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Tickets Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {!loading && tickets.length === 0 && (
                <Box sx={{ textAlign: 'center', p: 6 }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No feedback tickets found
                  </Typography>
                  <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                    Be the first to submit feedback or adjust your filters
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setSubmitModalOpen(true)}
                  >
                    Submit First Feedback
                  </Button>
                </Box>
              )}
              
              {!loading && tickets.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Submitted By</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tickets.map((ticket) => (
                        <TableRow
                          key={ticket.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => loadTicketDetail(ticket.id)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {ticket.title}
                              </Typography>
                              {(ticket.response_count || 0) > 0 && (
                                <Badge badgeContent={ticket.response_count} color="primary" />
                              )}
                              {(ticket.attachment_count || 0) > 0 && (
                                <AttachFileIcon fontSize="small" color="action" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={ticket.category} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={ticket.status} 
                              size="small" 
                              color={getStatusColor(ticket.status)} 
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={ticket.priority} 
                              size="small" 
                              color={getPriorityColor(ticket.priority)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {ticket.user_name || 'Anonymous'}
                            </Typography>
                            {ticket.user_email && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                {ticket.user_email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTimestamp(ticket.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTimestamp(ticket.updated_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Submit Feedback Modal */}
        <Dialog 
          open={submitModalOpen} 
          onClose={() => setSubmitModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Submit Feedback</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  value={newTicket.title}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Brief description of your feedback"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  required
                  multiline
                  rows={4}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed description of your feedback, issue, or request"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newTicket.category}
                    label="Category"
                    onChange={(e) => setNewTicket(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <MenuItem value="Question">Question</MenuItem>
                    <MenuItem value="Bug Report">Bug Report</MenuItem>
                    <MenuItem value="Feature Request">Feature Request</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newTicket.priority}
                    label="Priority"
                    onChange={(e) => setNewTicket(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Your Name (Optional)"
                  value={newTicket.user_name}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, user_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Your Email (Optional)"
                  type="email"
                  value={newTicket.user_email}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, user_email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.xls,.xlsx"
                  onChange={(e) => setNewTicketFiles(e.target.files)}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFileIcon />}
                  >
                    Attach Files
                  </Button>
                </label>
                {newTicketFiles && newTicketFiles.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {Array.from(newTicketFiles).map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmitModalOpen(false)}>Cancel</Button>
            <Button
              onClick={submitNewTicket}
              variant="contained"
              disabled={!newTicket.title || !newTicket.description}
            >
              Submit Feedback
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Detail View
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => setSelectedTicket(null)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              Ticket #{selectedTicket.id}
            </Typography>
            <Chip 
              label={selectedTicket.status} 
              color={getStatusColor(selectedTicket.status)} 
            />
            <Chip 
              label={selectedTicket.priority} 
              color={getPriorityColor(selectedTicket.priority)}
              variant="outlined"
            />
          </Box>
        </Grid>

        {/* Ticket Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {selectedTicket.title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {selectedTicket.description}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={selectedTicket.category} size="small" variant="outlined" />
              <Typography variant="caption" color="textSecondary">
                Created {formatTimestamp(selectedTicket.created_at)}
              </Typography>
              {selectedTicket.user_name && (
                <Typography variant="caption" color="textSecondary">
                  by {selectedTicket.user_name}
                </Typography>
              )}
            </Box>

            {/* Attachments */}
            {selectedTicket.attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Attachments:
                </Typography>
                {selectedTicket.attachments.map((attachment) => (
                  <Chip
                    key={attachment.id}
                    label={attachment.filename}
                    size="small"
                    clickable
                    onClick={() => window.open(`${API_BASE}/api/feedback/attachments/${attachment.file_path}`, '_blank')}
                    icon={<DownloadIcon />}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* Responses */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Responses ({selectedTicket.responses.length})
            </Typography>
            
            {selectedTicket.responses.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                No responses yet. Be the first to respond!
              </Typography>
            ) : (
              <List>
                {selectedTicket.responses.map((response, index) => (
                  <React.Fragment key={response.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">
                              {response.author_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatTimestamp(response.created_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {response.content}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < selectedTicket.responses.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}

            {/* Add Response */}
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" gutterBottom>
                Add Response
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Your Name"
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Response"
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Enter your response..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={addResponse}
                    disabled={!responseText.trim() || !authorName.trim()}
                  >
                    Send Response
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Ticket Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ticket Actions
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Update Status</InputLabel>
              <Select
                value={selectedTicket.status}
                label="Update Status"
                onChange={(e) => updateStatus(selectedTicket.id, e.target.value)}
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Ticket Information
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: #{selectedTicket.id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Created: {new Date(selectedTicket.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last Updated: {new Date(selectedTicket.updated_at).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Responses: {selectedTicket.responses.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Attachments: {selectedTicket.attachments.length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>
    </Container>
  );
};

export default Feedback;